import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  Alert,
  Autocomplete,
  Box,
  Chip,
  IconButton,
  ListItem,
} from "@mui/material";
import { closeModal, openModal } from "../../store/actions/modalAction";
import { callApiAction } from "../../store/actions/commonAction";
import {
  FetchPurchaseRequestApi,
  UpdatePurchaseRequestApi,
} from "../../apis/purchaseRequest.api";
import { findObjectKeyByValue, titleCase } from "../../utils/main";
import {
  CURRENCY,
  MATERIAL_REQUEST_STATUS,
  MATERIAL_TYPE,
  REQUEST_TYPE,
  SNACK_BAR_VARIETNS,
  USER_ROLES,
} from "../../utils/constants";
import moment from "moment";
import PurchaseRequestListUi from "./PurchaseRequestListUi";
import PurchaseRequestCreateController from "./PurchaseRequestCreateController";
import DirectPurchaseRequestController from "./DirectPurchaseRequestController";
import { CheckCircle, Cancel, Visibility } from "@mui/icons-material";
import { useParams } from "react-router-dom";
import PurchaseRequestViewController from "./PurchaseRequestViewController";
import { callSnackBar } from "../../store/actions/snackbarAction";
import MessageDilog from "../../components/MessageDilog";
import PurchaseOrderPlacementDialog from "./PurchaseOrderPlacementDialog";
import CustomDialog from "../../components/layouts/common/CustomDialog";
import CustomInput from "../../components/inputs/CustomInputs";

const PurchaseOrderReceiveDialog = ({ params, onClose, onSubmit }) => {
  const [transportationCharge, setTransportationCharge] = useState(0);
  const [dutyCharge, setDutyCharge] = useState(0);
  const [invoiceNumber, setInvoiceNumber] = useState(null);

  const [transportationCurrency, setTransportationCurrency] = useState(null);

  const [dutyCurrency, setDutyCurrency] = useState(null);
  const currencyOptions = Object.entries(CURRENCY).map(([label, value]) => ({
    label,
    value,
  }));

  return (
    <CustomDialog
      id="purchase-order-receive"
      title="Mark Order as Received"
      closeText="Cancel"
      confirmText="Mark as received"
      onClose={onClose}
      onSubmit={(e) => {
        e.preventDefault();
        onSubmit(
          Number(transportationCharge || 0),
          transportationCurrency,
          Number(dutyCharge || 0),
          dutyCurrency,
          invoiceNumber,
        );
      }}
    >
      <Box sx={{ display: "flex", gap: 2, mt: 1 }}>
        <CustomInput
          type="number"
          label="Transportation Charge"
          value={transportationCharge}
          min={0}
          onChange={(e) => setTransportationCharge(e.target.value)}
          onWheel={(e) => e.target.blur()}
        />

        <Autocomplete
          fullWidth
          options={currencyOptions}
          value={transportationCurrency}
          onChange={(e, changedVal) => setTransportationCurrency(changedVal)}
          getOptionLabel={(option) => option?.label || ""}
          isOptionEqualToValue={(option, value) =>
            option?.value === value?.value
          }
          renderOption={(props, option) => (
            <ListItem {...props}>{option.label}</ListItem>
          )}
          renderInput={(params) => (
            <CustomInput
              {...params}
              label="Select Currency"
              placeholder="Select Currency"
              margin="dense"
              InputLabelProps={{ shrink: true }}
            />
          )}
        />
      </Box>

      <Box sx={{ display: "flex", gap: 2, mt: 1 }}>
        <CustomInput
          type="number"
          label="Duty Charge"
          value={dutyCharge}
          min={0}
          onChange={(e) => setDutyCharge(e.target.value)}
          onWheel={(e) => e.target.blur()}
        />

        <Autocomplete
          fullWidth
          options={currencyOptions}
          value={dutyCurrency}
          onChange={(e, changedVal) => setDutyCurrency(changedVal)}
          getOptionLabel={(option) => option?.label || ""}
          isOptionEqualToValue={(option, value) =>
            option?.value === value?.value
          }
          renderOption={(props, option) => (
            <ListItem {...props}>{option.label}</ListItem>
          )}
          renderInput={(params) => (
            <CustomInput
              {...params}
              label="Select Currency"
              placeholder="Select Currency"
              margin="dense"
              InputLabelProps={{ shrink: true }}
            />
          )}
        />
      </Box>

      <Box sx={{ display: "flex", gap: 2, mt: 1 }}>
        <CustomInput
          type="text"
          label="Invoice Number"
          value={invoiceNumber}
          onChange={(e) => setInvoiceNumber(e.target.value)}
        />
      </Box>
    </CustomDialog>
  );
};

const ActionComponent = ({ params, onApprove, onReject, onView }) => {
  const { status, requestType } = params;
  const { user } = useSelector((state) => state);

  let showApproveButton = false;
  let nextStatusForApproval = null;
  let actionTextForApproval = "";

  if (
    status === MATERIAL_REQUEST_STATUS.PENDING &&
    user.data.isDepartmentHead &&
    user.data.role === params?.requestCreator?.role
  ) {
    console.log("User is department head and can approve the request.");
    showApproveButton = true;
    nextStatusForApproval = MATERIAL_REQUEST_STATUS.APPROVAL_1;
    actionTextForApproval = "approve";
  } else if (
    requestType === REQUEST_TYPE.PURCHASE &&
    (status === MATERIAL_REQUEST_STATUS.APPROVAL_1 ||
      status === MATERIAL_REQUEST_STATUS.DIRECT_APPROVED) &&
    user.data.isDepartmentHead &&
    user.data.role === USER_ROLES.PURCHASE
  ) {
    showApproveButton = true;
    nextStatusForApproval = MATERIAL_REQUEST_STATUS.ORDER_PLACED;
    actionTextForApproval = "place order";
  } else if (
    requestType === REQUEST_TYPE.PURCHASE &&
    status === MATERIAL_REQUEST_STATUS.ORDER_PLACED
  ) {
    if (
      user.data.role === USER_ROLES.PURCHASE &&
      user?.data?.isDepartmentHead
    ) {
      showApproveButton = true;
      nextStatusForApproval = MATERIAL_REQUEST_STATUS.ORDER_RECEIVED;
      actionTextForApproval = "mark as received";
    }
  } else if (
    user.data.authorised &&
    ((requestType === REQUEST_TYPE.MATERIAL &&
      status === MATERIAL_REQUEST_STATUS.APPROVAL_1 &&
      !params?.isDirectPurchase) ||
      (requestType === REQUEST_TYPE.PURCHASE &&
        status === MATERIAL_REQUEST_STATUS.ORDER_RECEIVED &&
        !params?.isDirectPurchase))
  ) {
    showApproveButton = true;
    nextStatusForApproval = MATERIAL_REQUEST_STATUS.ALLOCATED;
    actionTextForApproval = "allocate";
  } else if (
    status === MATERIAL_REQUEST_STATUS.ALLOCATED &&
    user.data._id === params?.requestCreatedBy
  ) {
    showApproveButton = true;
    nextStatusForApproval = MATERIAL_REQUEST_STATUS.MATERIAL_RECEIVED;
    actionTextForApproval = "Material received";
  }

  return (
    <>
      {showApproveButton &&
        (status === MATERIAL_REQUEST_STATUS.ALLOCATED ||
          user.data._id !== params?.requestCreator?._id) && (
          <>
            <IconButton
              color="success"
              onClick={() =>
                onApprove(params, nextStatusForApproval, actionTextForApproval)
              }
            >
              <CheckCircle />
            </IconButton>
            <IconButton color="error" onClick={() => onReject(params)}>
              <Cancel />
            </IconButton>
          </>
        )}
      <IconButton color="primary" onClick={() => onView(params)}>
        <Visibility />
      </IconButton>
    </>
  );
};

const PurchaseRequestListController = () => {
  const dispatch = useDispatch();
  const params = useParams();
  const [list, setList] = useState({});
  const [loading, setLoading] = useState(false);
  const [filters, setFilters] = useState({
    pageNo: 1,
    pageSize: 10,
    search: "",
    searchable: ["name", "requestId"],
    sort: "",
    sortDirection: -1,
  });

  const currentFilters = useRef(filters);

  useEffect(() => {
    currentFilters.current = filters;
  }, [filters]);
  const fetchList = useCallback(() => {
    setLoading(true);
    dispatch(
      callApiAction(
        () => FetchPurchaseRequestApi({ ...currentFilters.current }),
        (res) => {
          setList(res);
          setLoading(false);
        },
        () => setLoading(false),
      ),
    );
  }, [dispatch, filters]);

  const confirmApprove = useCallback(
    (
      params,
      nextStatus,
      selectedVendor = null,
      amount = null,
      selectedMaterial = null,
      transportationCharge = 0,
      currency = null,
      transportationCurrency = null,
      dutyCharge = 0,
      dutyCurrency = null,
      invoiceNumber = null,
    ) => {
      console.log("Confirming approval with the following parameters:", {
        params,
        nextStatus,
        selectedVendor,
        amount,
        selectedMaterial,
        transportationCharge,
        currency,
        transportationCurrency,
        dutyCharge,
        dutyCurrency,
        invoiceNumber,
      });
      dispatch(
        callApiAction(
          async () =>
            await UpdatePurchaseRequestApi({
              id: params._id,
              status: nextStatus,
              ...(selectedVendor ? { vendorId: selectedVendor._id } : {}),
              ...(amount !== null && amount !== ""
                ? { amount: Number(amount) }
                : {}),
              ...(nextStatus === MATERIAL_REQUEST_STATUS.ORDER_RECEIVED
                ? { transportationCharge: Number(transportationCharge || 0) }
                : {}),
              ...(selectedMaterial && selectedMaterial._id
                ? { materialId: selectedMaterial._id }
                : {}),
              ...(currency ? { currency: currency.value } : {}),

              ...(nextStatus === MATERIAL_REQUEST_STATUS.ORDER_RECEIVED
                ? {
                    transportationCurrency:
                      transportationCurrency?.value &&
                      Number(transportationCurrency?.value),
                  }
                : {}),

              ...(nextStatus === MATERIAL_REQUEST_STATUS.ORDER_RECEIVED
                ? { dutyCharge: Number(dutyCharge || 0) }
                : {}),

              ...(nextStatus === MATERIAL_REQUEST_STATUS.ORDER_RECEIVED
                ? {
                    dutyCurrency:
                      dutyCurrency?.value && Number(dutyCurrency?.value),
                  }
                : {}),
              ...(invoiceNumber ? { invoiceNumber } : {}),
            }),
          async () => {
            dispatch(closeModal("purchase-order-receive"));
            dispatch(closeModal("purchase-order-placement"));
            dispatch(closeModal("messagedialog"));
            fetchList();
          },
          (err) => {
            dispatch(callSnackBar(err, SNACK_BAR_VARIETNS.error));
          },
        ),
      );
    },
    [dispatch, fetchList],
  );

  const confirmReject = useCallback(
    (e, params) => {
      if (e && typeof e.preventDefault === "function") {
        e.preventDefault();
      }
      dispatch(
        callApiAction(
          async () =>
            await UpdatePurchaseRequestApi({
              id: params._id,
              status: MATERIAL_REQUEST_STATUS.REJECTED,
            }),
          async () => {
            fetchList();
            dispatch(closeModal("messagedialog"));
          },
          (err) => {
            dispatch(callSnackBar(err, SNACK_BAR_VARIETNS.error));
          },
        ),
      );
    },
    [dispatch, fetchList],
  );

  const onView = useCallback(
    (params) => {
      dispatch(
        openModal(
          <PurchaseRequestViewController params={params} />,
          "md",
          false,
          "purchase-view",
        ),
      );
    },
    [dispatch],
  );

  const handleApprove = useCallback(
    (params, nextStatus, actionText) => {
      if (nextStatus === MATERIAL_REQUEST_STATUS.ORDER_PLACED) {
        dispatch(
          openModal(
            <PurchaseOrderPlacementDialog
              params={params}
              onClose={() => dispatch(closeModal("purchase-order-placement"))}
              onSubmit={({ vendor, amount, material, currency }) =>
                confirmApprove(
                  params,
                  nextStatus,
                  vendor,
                  amount,
                  material,
                  null,
                  currency,
                  null,
                  null,
                  null,
                )
              }
            />,
            "sm",
            false,
            "purchase-order-placement",
          ),
        );
        return;
      }

      if (nextStatus === MATERIAL_REQUEST_STATUS.ORDER_RECEIVED) {
        dispatch(
          openModal(
            <PurchaseOrderReceiveDialog
              params={params}
              onClose={() => dispatch(closeModal("purchase-order-receive"))}
              onSubmit={(
                transportationCharge,
                transportationCurrency,
                dutyCharge,
                dutyCurrency,
                invoiceNumber,
              ) =>
                confirmApprove(
                  params,
                  nextStatus,
                  null,
                  null,
                  null,
                  transportationCharge,
                  null,
                  transportationCurrency,
                  dutyCharge,
                  dutyCurrency,
                  invoiceNumber,
                )
              }
            />,
            "sm",
            false,
            "purchase-order-receive",
          ),
        );
        return;
      }

      dispatch(
        openModal(
          <MessageDilog
            onSubmit={(e) => {
              if (e && e.preventDefault) e.preventDefault();
              confirmApprove(params, nextStatus);
            }}
            title="Confirm Action"
            modalId="messagedialog"
            message={`Are you sure you want to ${actionText} "${params?.materialDetails?.name || params?.otherMaterialName}"?`}
            confirmText={titleCase(actionText)}
          />,
          "sm",
          false,
          "messagedialog",
        ),
      );
    },
    [dispatch, confirmApprove],
  );

  const handleReject = useCallback(
    (params) => {
      dispatch(
        openModal(
          <MessageDilog
            onSubmit={(e) => confirmReject(e, params)}
            title="Alert!"
            modalId="messagedialog"
            message={`Are you sure you want to reject "${params?.materialDetails?.name || params?.otherMaterialName}" ?`}
            confirmText={"Reject"}
          />,
          "sm",
          false,
          "messagedialog",
        ),
      );
    },
    [dispatch, confirmReject],
  );

  useEffect(() => {
    fetchList();
  }, [fetchList]);

  const getStatusLabelAndColor = (status) => {
    switch (status) {
      case MATERIAL_REQUEST_STATUS.PENDING:
        return { label: "Pending", color: "default" };
      case MATERIAL_REQUEST_STATUS.APPROVAL_1:
        return { label: "Approved", color: "primary" };
      case MATERIAL_REQUEST_STATUS.ORDER_PLACED:
        return { label: "Order Placed", color: "warning" };
      case MATERIAL_REQUEST_STATUS.ALLOCATED:
        return { label: "Allocated", color: "success" };
      case MATERIAL_REQUEST_STATUS.REJECTED:
        return { label: "Rejected", color: "error" };
      case MATERIAL_REQUEST_STATUS.ORDER_RECEIVED:
        return { label: "Order Received", color: "secondary" };
      case MATERIAL_REQUEST_STATUS.DIRECT_APPROVED:
        return { label: "Direct Approved", color: "success" };
      case MATERIAL_REQUEST_STATUS.MATERIAL_RECEIVED:
        return { label: "Material Received", color: "success" };
      default:
        return { label: "NA", color: "default" };
    }
  };

  const columns = useMemo(
    () => [
      {
        id: 1,
        fieldName: "",
        label: "Date",
        align: "left",
        sort: true,
        renderValue: (params) =>
          moment(params?.date).format("DD/MM/YYYY") || "NA",
      },
      {
        id: 2,
        fieldName: "",
        label: "Request Type",
        align: "left",
        sort: true,
        renderValue: (params) =>
          findObjectKeyByValue(params?.requestType, REQUEST_TYPE) || "NA",
      },
      {
        id: 9,
        fieldName: "",
        label: "Material Request ID",
        align: "left",
        sort: true,
        renderValue: (params) => params?.requestId || "NA",
      },
      {
        id: 10,
        fieldName: "",
        label: "Material Type",
        align: "left",
        sort: true,
        renderValue: (params) =>
          findObjectKeyByValue(params?.materialType, MATERIAL_TYPE) || "NA",
      },
      {
        id: 3,
        fieldName: "",
        label: "Material Name",
        align: "left",
        sort: true,
        renderValue: (params) => params?.materialDetails?.name || "Other",
      },

      {
        id: 4,
        fieldName: "",
        label: "Other Material Name",
        align: "left",
        sort: true,
        renderValue: (params) => params?.otherMaterialName || "NA",
      },

      {
        id: 5,
        fieldName: "",
        label: "Quantity",
        align: "left",
        sort: true,
        renderValue: (params) => params?.quantity || "NA",
      },

      {
        id: 8,
        fieldName: "",
        label: "Amount",
        align: "left",
        sort: true,
        renderValue: (params) => params?.amount || "NA",
      },
      {
        id: 4,
        fieldName: "",
        label: "Status",
        align: "left",
        sort: true,
        renderValue: (params) => {
          const { label, color } = getStatusLabelAndColor(params?.status);
          return <Chip label={label} color={color} size="small" />;
        },
      },
      {
        id: 7,
        fieldName: "",
        label: "Action",
        align: "left",
        sort: false,
        renderValue: (params) => (
          <ActionComponent
            params={params}
            onApprove={handleApprove}
            onReject={handleReject}
            onView={onView}
          />
        ),
      },
    ],
    [handleApprove, handleReject, onView],
  );

  const onCreateBtnClick = () => {
    dispatch(
      openModal(
        <PurchaseRequestCreateController
          id={params._id}
          callBack={fetchList}
        />,
        "lg",
        false,
        "purchase",
      ),
    );
  };

  const onDirectPurchaseBtnClick = () => {
    dispatch(
      openModal(
        <DirectPurchaseRequestController
          callBack={fetchList}
          onClose={() => dispatch(closeModal("direct-purchase"))}
        />,
        "lg",
        false,
        "direct-purchase",
      ),
    );
  };

  return (
    <PurchaseRequestListUi
      onCreateBtnClick={onCreateBtnClick}
      onDirectPurchaseBtnClick={onDirectPurchaseBtnClick}
      list={list}
      loading={loading}
      filters={filters}
      setFilters={setFilters}
      columns={columns}
    />
  );
};

export default PurchaseRequestListController;
